import { useMemo } from "react";

import { useGetS3Files } from "./useGetS3Files";

export const RECORDINGS_AVAILABILITY_SENTINEL = "__availability__";

const parseDatePath = (
  path: string
): {
  isoKey: string;
  date: Date;
} | null => {
  const parts = path.split("/");

  if (parts.length !== 3) {
    return null;
  }

  const [yearStr, monthStr, dayStr] = parts;

  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);

  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day)
  ) {
    return null;
  }

  try {
    const date = new Date(Date.UTC(year, month - 1, day));
    const isoKey = `${year.toString().padStart(4, "0")}-${month
      .toString()
      .padStart(2, "0")}-${day.toString().padStart(2, "0")}`;

    return { isoKey, date };
  } catch {
    return null;
  }
};

type UseRecordingsAvailableDatesOptions = {
  skip?: boolean;
};

export const useRecordingsAvailableDates = (
  deviceIdClean: string,
  nodeIdClean: string,
  options?: UseRecordingsAvailableDatesOptions
) => {
  const { availableDates, availableDatesWithCounts, loading, error, refetch } =
    useGetS3Files(
      deviceIdClean,
      nodeIdClean,
      RECORDINGS_AVAILABILITY_SENTINEL,
      {
        skip: !deviceIdClean || !nodeIdClean || options?.skip,
      }
    );

  const { orderedDates, isoKeySet } = useMemo(() => {
    if (!availableDates?.length) {
      return { orderedDates: [] as Date[], isoKeySet: new Set<string>() };
    }

    const parsed = availableDates.map(parseDatePath).filter(Boolean) as Array<{
      isoKey: string;
      date: Date;
    }>;

    parsed.sort((a, b) => a.date.getTime() - b.date.getTime());

    const dates = parsed.map(entry => entry.date);
    const keys = new Set(parsed.map(entry => entry.isoKey));

    return { orderedDates: dates, isoKeySet: keys };
  }, [availableDates]);

  const earliestDate = orderedDates.length > 0 ? orderedDates[0] : undefined;
  const latestDate =
    orderedDates.length > 0 ? orderedDates[orderedDates.length - 1] : undefined;

  // Create a map of date paths to counts
  const dateCountMap = useMemo(() => {
    const map = new Map<string, number>();

    if (availableDatesWithCounts) {
      for (const item of availableDatesWithCounts) {
        map.set(item.date, item.count);
      }
    }

    return map;
  }, [availableDatesWithCounts]);

  return {
    loading,
    error,
    refetch,
    availableDatePaths: availableDates,
    availableDateSet: isoKeySet,
    availableDateObjects: orderedDates,
    earliestDate,
    latestDate,
    dateCountMap,
  };
};
